import React, { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, X, ChevronLeft, ChevronRight } from "lucide-react";

// ----------------------- DATA -----------------------
// Categories with subcategories. Replace `src` URLs as new photos arrive.
const PHOTOS = [
  // ===== Decor — Floral =====
  {
    id: "floral-2",
    src: "/gallery/floral-2.jpg",
    category: "decor",
    sub: "floral",
    caption: "Blush floral arch with fairy lights",
    h: 4,
  },
  {
    id: "floral-4",
    src: "/gallery/floral-4.jpg",
    category: "decor",
    sub: "floral",
    caption: "Floral archway installation",
    h: 6,
  },
  {
    id: "floral-5",
    src: "/gallery/floral-5.jpg",
    category: "decor",
    sub: "floral",
    caption: "Custom floral backdrop",
    h: 6,
  },
  {
    id: "floral-8",
    src: "/gallery/floral-8.jpg",
    category: "decor",
    sub: "floral",
    caption: "Red rose archway with candlelight",
    h: 5,
  },
  {
    id: "floral-10",
    src: "/gallery/floral-10.jpg",
    category: "decor",
    sub: "floral",
    caption: "White rose cascade over draped curtains",
    h: 5,
  },
  {
    id: "floral-12",
    src: "/gallery/floral-12.jpg",
    category: "decor",
    sub: "floral",
    caption: "Hassan & Sophie-Esha welcome floral arch",
    h: 6,
  },
  {
    id: "floral-13",
    src: "/gallery/floral-13.jpg",
    category: "decor",
    sub: "floral",
    caption: "Pampas & white rose circular arch",
    h: 5,
  },
  {
    id: "floral-15",
    src: "/gallery/floral-15.jpg",
    category: "decor",
    sub: "floral",
    caption: "Gold-framed stage with 'I♥G' neon accent",
    h: 6,
  },

  // ===== Decor — Balloon =====
  {
    id: "balloon-1",
    src: "/gallery/balloon-2.jpg",
    category: "decor",
    sub: "balloon",
    caption: "Strawberry-themed birthday balloon arches",
    h: 6,
  },
  {
    id: "balloon-2",
    src: "/gallery/balloon-3.jpg",
    category: "decor",
    sub: "balloon",
    caption: "Baby shower balloon arch — 'Omar & Faris'",
    h: 6,
  },
  {
    id: "balloon-3",
    src: "/gallery/balloon-4.jpg",
    category: "decor",
    sub: "balloon",
    caption: "Custom balloon installation",
    h: 6,
  },
  {
    id: "balloon-4",
    src: "/gallery/balloon-1.jpg",
    category: "decor",
    sub: "balloon",
    caption: "Event balloon styling",
    h: 4,
  },
  {
    id: "balloon-5",
    src: "/gallery/balloon-5.jpg",
    category: "decor",
    sub: "balloon",
    caption: "Balloon decor setup",
    h: 5,
  },
  {
    id: "balloon-6",
    src: "/gallery/balloon-6.jpg",
    category: "decor",
    sub: "balloon",
    caption: "Custom balloon installation",
    h: 6,
  },
  {
    id: "balloon-7",
    src: "/gallery/balloon-7.jpg",
    category: "decor",
    sub: "balloon",
    caption: "Balloon styling for celebration",
    h: 6,
  },
  {
    id: "balloon-8",
    src: "/gallery/balloon-8.jpg",
    category: "decor",
    sub: "balloon",
    caption: "Themed balloon arch",
    h: 6,
  },
  {
    id: "balloon-9",
    src: "/gallery/balloon-9.jpg",
    category: "decor",
    sub: "balloon",
    caption: "Event balloon decor",
    h: 6,
  },
  {
    id: "balloon-10",
    src: "/gallery/balloon-10.jpg",
    category: "decor",
    sub: "balloon",
    caption: "Signature balloon backdrop",
    h: 6,
  },

  // ===== AV Setups =====
  {
    id: "av-1",
    src: "https://customer-assets.emergentagent.com/job_karnival-preview/artifacts/ml2gpzc0_IMG_1070.JPG",
    category: "av",
    sub: null,
    caption: "DJ Avtaar in action",
    h: 5,
  },
  {
    id: "av-2",
    src: "/gallery/av-2.jpg",
    category: "av",
    sub: null,
    caption: "Blue uplighting & DJ booth setup",
    h: 5,
  },
  {
    id: "av-3",
    src: "/gallery/av-3.jpg",
    category: "av",
    sub: null,
    caption: "First-dance lighting design",
    h: 5,
  },
  {
    id: "av-4",
    src: "/gallery/av-4.jpg",
    category: "av",
    sub: null,
    caption: "Mobile baraat — DJ truck procession",
    h: 5,
  },

  // ===== Special FX =====
  {
    id: "fx-1",
    src: "https://customer-assets.emergentagent.com/job_karnival-preview/artifacts/qifmu7od_IMG_9683.JPG",
    category: "fx",
    sub: null,
    caption: "Cold spark fountains during first dance",
    h: 5,
  },
  {
    id: "fx-2",
    src: "https://customer-assets.emergentagent.com/job_karnival-preview/artifacts/j2ilg9uh_image.png",
    category: "fx",
    sub: null,
    caption: "Sparks + dancing-on-clouds fog effect",
    h: 6,
  },
  {
    id: "fx-3",
    src: "/gallery/fx-3.jpg",
    category: "fx",
    sub: null,
    caption: "CO₂ cannon blast over the dancefloor",
    h: 5,
  },
];

const TOP_FILTERS = [
  { key: "all", label: "All Work" },
  { key: "decor", label: "Decor Setups" },
  { key: "av", label: "AV Setups" },
  { key: "fx", label: "Special FX" },
];

const DECOR_SUBS = [
  { key: "decor", label: "All Decor" },
  { key: "decor:floral", label: "Floral Decor" },
  { key: "decor:balloon", label: "Balloon Decor" },
];

// ----------------------- COMPONENT -----------------------
export default function Gallery() {
  const [active, setActive] = useState("all");
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Respect ?filter= query (e.g. /gallery?filter=decor or ?filter=decor:floral)
  useEffect(() => {
    const q = searchParams.get("filter");
    const valid = ["all", "decor", "decor:floral", "decor:balloon", "av", "fx"];
    if (q && valid.includes(q)) setActive(q);
  }, [searchParams]);

  const filtered = useMemo(() => {
    if (active === "all") return PHOTOS;
    if (active === "decor") return PHOTOS.filter((p) => p.category === "decor");
    if (active === "decor:floral")
      return PHOTOS.filter((p) => p.category === "decor" && p.sub === "floral");
    if (active === "decor:balloon")
      return PHOTOS.filter((p) => p.category === "decor" && p.sub === "balloon");
    if (active === "av") return PHOTOS.filter((p) => p.category === "av");
    if (active === "fx") return PHOTOS.filter((p) => p.category === "fx");
    return PHOTOS;
  }, [active]);

  // Lightbox handlers
  const openLightbox = (i) => setLightboxIdx(i);
  const closeLightbox = () => setLightboxIdx(null);
  const next = (e) => {
    e?.stopPropagation();
    setLightboxIdx((i) => (i === null ? null : (i + 1) % filtered.length));
  };
  const prev = (e) => {
    e?.stopPropagation();
    setLightboxIdx((i) =>
      i === null ? null : (i - 1 + filtered.length) % filtered.length
    );
  };

  // Distribute photos into 3 masonry columns deterministically
  const columns = useMemo(() => {
    const cols = [[], [], []];
    filtered.forEach((p, i) => cols[i % 3].push(p));
    return cols;
  }, [filtered]);

  return (
    <div className="bg-[#070707] min-h-screen text-[#FDFBF7]" data-testid="gallery-page">
      {/* Hero */}
      <section className="relative pt-40 pb-16 md:pt-48 md:pb-20 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto">
          <button
            onClick={() => navigate("/")}
            data-testid="gallery-back"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors mb-10"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to home
          </button>

          <div className="flex items-center gap-3 mb-5">
            <div className="h-px w-10 bg-white" />
            <span className="text-xs uppercase tracking-[0.28em] text-white">
              Our Work
            </span>
          </div>

          <h1
            data-testid="gallery-headline"
            className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.02] tracking-tight max-w-3xl"
          >
            A look inside the rooms we&rsquo;ve{" "}
            <span className="italic text-white/85">transformed</span>.
          </h1>

          <p className="mt-8 text-white/65 text-base md:text-lg leading-relaxed max-w-2xl">
            Browse setups by category — from floral installations and balloon
            backdrops to full AV rigs. Click any image to open it full-screen.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="px-6 md:px-12 mb-12 sticky top-24 md:top-28 z-30">
        <div className="max-w-[1400px] mx-auto space-y-3">
          {/* Top-level filters */}
          <div
            className="flex flex-wrap gap-2 md:gap-3 bg-[#0a0a0a]/95 backdrop-blur-xl border border-white/10 rounded-sm p-2"
            data-testid="gallery-filters"
          >
            {TOP_FILTERS.map((f) => {
              const isActive =
                active === f.key ||
                (f.key === "decor" && active.startsWith("decor"));
              return (
                <button
                  key={f.key}
                  onClick={() => setActive(f.key)}
                  data-testid={`gallery-filter-${f.key}`}
                  className={`text-sm px-4 py-2 rounded-sm transition-all duration-200 ${
                    isActive
                      ? "bg-white text-black"
                      : "text-white/60 hover:text-white hover:bg-white/5"
                  }`}
                >
                  {f.label}
                </button>
              );
            })}
            <div className="ml-auto self-center text-xs text-white/40 pr-3 hidden md:block">
              {filtered.length} {filtered.length === 1 ? "image" : "images"}
            </div>
          </div>

          {/* Decor sub-filters (only when decor is active) */}
          <AnimatePresence>
            {active.startsWith("decor") && (
              <motion.div
                key="decor-subs"
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.25 }}
                className="flex flex-wrap items-center gap-2 md:gap-3 pl-3"
                data-testid="gallery-subfilters"
              >
                <span className="text-[11px] uppercase tracking-[0.22em] text-white/40 mr-1">
                  Refine
                </span>
                {DECOR_SUBS.map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setActive(s.key)}
                    data-testid={`gallery-subfilter-${s.key.replace(":", "-")}`}
                    className={`text-xs px-3.5 py-1.5 rounded-full border transition-all duration-200 ${
                      active === s.key
                        ? "bg-white text-black border-white"
                        : "text-white/60 border-white/15 hover:text-white hover:border-white/40"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Masonry grid */}
      <section className="px-6 md:px-12 pb-32">
        <div className="max-w-[1400px] mx-auto">
          {filtered.length === 0 ? (
            <div className="text-center py-32 text-white/50">
              No images yet — check back soon.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {columns.map((col, ci) => (
                <div key={ci} className="flex flex-col gap-5 md:gap-6">
                  {col.map((p) => {
                    const idx = filtered.findIndex((x) => x.id === p.id);
                    return (
                      <motion.button
                        key={p.id}
                        layout
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        onClick={() => openLightbox(idx)}
                        data-testid={`gallery-item-${p.id}`}
                        className="group relative w-full overflow-hidden rounded-sm border border-white/5 hover:border-white/30 transition-colors text-left"
                        style={{
                          aspectRatio: `4 / ${p.h}`,
                        }}
                      >
                        <img
                          src={p.src}
                          alt={p.caption}
                          loading="lazy"
                          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1500ms] ease-out"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-transparent opacity-60 group-hover:opacity-90 transition-opacity duration-500" />
                        <div className="absolute bottom-4 left-4 right-4 translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
                          <div className="text-[10px] uppercase tracking-[0.24em] text-white/60">
                            {p.category === "decor"
                              ? `Decor · ${p.sub === "floral" ? "Floral" : "Balloon"}`
                              : p.category === "fx"
                                ? "Special FX"
                                : "AV Setup"}
                          </div>
                          <div className="text-white text-sm mt-1 font-medium">
                            {p.caption}
                          </div>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          <div className="mt-20 text-center">
            <Link
              to="/contact"
              data-testid="gallery-cta"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white text-black font-medium hover:bg-white/90 transition-colors duration-300"
            >
              Plan an Event Like These
            </Link>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIdx !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={closeLightbox}
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-12"
            data-testid="gallery-lightbox"
          >
            <button
              onClick={closeLightbox}
              data-testid="lightbox-close"
              aria-label="Close"
              className="absolute top-6 right-6 z-10 h-11 w-11 rounded-full border border-white/20 text-white/80 hover:text-black hover:bg-white hover:border-white flex items-center justify-center transition-all"
            >
              <X className="h-5 w-5" />
            </button>

            <button
              onClick={prev}
              data-testid="lightbox-prev"
              aria-label="Previous"
              className="absolute left-4 md:left-8 z-10 h-12 w-12 rounded-full border border-white/20 text-white/80 hover:text-black hover:bg-white hover:border-white flex items-center justify-center transition-all"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>

            <button
              onClick={next}
              data-testid="lightbox-next"
              aria-label="Next"
              className="absolute right-4 md:right-8 z-10 h-12 w-12 rounded-full border border-white/20 text-white/80 hover:text-black hover:bg-white hover:border-white flex items-center justify-center transition-all"
            >
              <ChevronRight className="h-5 w-5" />
            </button>

            <motion.figure
              key={filtered[lightboxIdx].id}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-[90vw] max-h-[88vh] flex flex-col items-center"
            >
              <img
                src={filtered[lightboxIdx].src}
                alt={filtered[lightboxIdx].caption}
                className="max-h-[80vh] max-w-full object-contain"
              />
              <figcaption className="mt-5 text-sm text-white/70 tracking-wide">
                {filtered[lightboxIdx].caption}
                <span className="text-white/40 ml-3">
                  {lightboxIdx + 1} / {filtered.length}
                </span>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
